import { useCallback, useEffect } from 'react';
import organizationsService from '../services/organizationsService';
import {
  setOrganizations,
  setOrganizationsLoaded,
  useOrganizationsStore,
} from '../store/organizationsStore';

/**
 * Loads the signed-in user's organisations once, on the same lifecycle as
 * `useTeams` (plan §5, Phase 4a).
 *
 * Here rather than in each component for the reason teams are: half a dozen
 * surfaces read this list — the Organisations page, the team settings row, and
 * anything that hides itself for an org-less user — and a fetch per component
 * would be that many requests for a record that changes about once a year.
 *
 * Cleared on sign-out. A stale list belonging to a previous account is worse
 * than an empty one: it would show another user's organisation names until
 * something happened to refetch.
 */
export function useOrganizations({ isAuthenticated }) {
  const reload = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setOrganizations(await organizationsService.getOrganizations());
    } catch {
      // Keep whatever was already loaded. A transient failure must not empty
      // the list and make every org-gated surface disappear.
    } finally {
      setOrganizationsLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      reload();
    } else {
      useOrganizationsStore.getState().reset();
    }
  }, [isAuthenticated, reload]);

  return { reloadOrganizations: reload };
}
